import classNames from 'classnames';
import React from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { MenuItem, MenuSeparator } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import arrowRightIcon from '../icons/arrowRight.svg';
import editIcon from '../icons/edit.svg';
import overflowMenuIcon from '../icons/overflowMenu.svg';
import templateIcon from '../icons/prefilled/template.svg';
import trashIcon from '../icons/trash.svg';
import topicPolicyCheck, { TopicPolicyAction } from '../topic/topicPolicyCheck';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import { createPostPath } from '../utils/url/postPath';
import styles from './TemplateHeader.module.scss';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const TEMPLATE_HEADER_TOPIC_FRAGMENT = gql(`
  fragment TemplateHeader_topic on Topic {
    id
    name
    ...topicPolicyCheck_topic
  }
`);

const TEMPLATE_HEADER_OWNER_FRAGMENT = gql(`
  fragment TemplateHeader_owner on User {
    id
    name
  }
`);

interface TemplateHeaderProps {
  templateId: string;
  topics: DocumentType<typeof TEMPLATE_HEADER_TOPIC_FRAGMENT>[];
  owner: DocumentType<typeof TEMPLATE_HEADER_OWNER_FRAGMENT> | null;
  onEditTemplate: () => void;
  onChangeLocation: () => void;
  onDelete: () => void;
  onCreatePost?: () => void;
}

const TemplateHeader = ({
  templateId,
  topics,
  owner,
  onEditTemplate,
  onChangeLocation,
  onDelete,
  onCreatePost,
}: TemplateHeaderProps) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const isMobile = useIsSize(Sizes.SM);
  const topic = topics[0];

  const canEdit = topic
    ? topicPolicyCheck(TopicPolicyAction.EditPost, topic, currentUser)
    : true;

  const disabledMenuItemProps =
    topic && !canEdit
      ? {
          disabled: true,
          tooltip: `Only editors of ${topic.name} can edit this template`,
        }
      : {};

  const tooltip = (() => {
    if (topics.length > 1) {
      return 'Accessible to members of these topics';
    }

    if (topic) {
      return `Accessible to members of ${topic.name}`;
    }

    if (owner) {
      return 'Accessible only to you';
    }

    return null;
  })();

  return (
    <div className={styles.header}>
      <span className={styles.iconContainer}>
        <SVGIcon inline>{templateIcon}</SVGIcon>
      </span>
      <div>
        <span className={styles.templateText}>Template</span>
        &nbsp;·&nbsp;
      </div>
      <div className={styles.templateOwnerWrapper}>
        <Tooltip
          tooltip={tooltip}
          placement="top"
          trigger="hover"
          display="inline-block"
        >
          <div className={styles.templateOwner}>
            {topics.length > 0
              ? topics.map((t) => t.name).join(' · ')
              : 'Personal'}
          </div>
        </Tooltip>
      </div>
      <div className={styles.menu}>
        <OverflowMenu
          button={
            <Button className={styles.overflowMenuButton}>
              <SVGIcon size={16} coloringWith="fill">
                {overflowMenuIcon}
              </SVGIcon>
            </Button>
          }
        >
          {isMobile && (
            <MenuItem
              icon={editIcon}
              onClick={onCreatePost}
              to={createPostPath({ templateId })}
            >
              Create post
            </MenuItem>
          )}
          <MenuItem
            icon={editIcon}
            onClick={() => {
              onEditTemplate();
            }}
            {...disabledMenuItemProps}
          >
            Edit template
          </MenuItem>
          <MenuItem
            icon={arrowRightIcon}
            onClick={() => {
              onChangeLocation();
            }}
            {...disabledMenuItemProps}
          >
            Change location
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            danger
            icon={trashIcon}
            onClick={onDelete}
            {...disabledMenuItemProps}
          >
            Delete template
          </MenuItem>
        </OverflowMenu>
        {!isMobile && (
          <Button
            className={classNames(styles.menuButton, styles.createPostButton)}
            variant="primary"
            size="small"
            onClick={onCreatePost}
            to={createPostPath({ templateId })}
          >
            Create post
          </Button>
        )}
      </div>
    </div>
  );
};

export default TemplateHeader;
